import { useEffect, useMemo, useState } from 'react';
import {
    Accordion, AccordionDetails, AccordionSummary, Alert, Autocomplete, Avatar, Box, Button, Chip,
    CircularProgress, Divider, FormControlLabel, Stack, Switch, TextField, Typography
} from '@mui/material';
import { CloudDownload, ExpandMore } from '@mui/icons-material';
import { useIFood } from '../../../../../marketplace/hooks/useIFood';
import { useVinculosMarketplace } from '../../../../../marketplace/hooks/useVinculosMarketplace';
import { ICategoriaIFood, IGrupoOpcoesIFood, IItemIFood, IOpcaoIFood, IPrecoIFood } from '../../../../../marketplace/interfaces/ICategoriaIFood';
import { IVinculoMarketplaceProduto, TipoElementoVinculo } from '../../../../../marketplace/interfaces/IVinculoMarketplaceProduto';
import { TipoMarketplace } from '../../../../../marketplace/enum/TipoMarketplace';
import { useFetchCategorias } from '../../../../hooks/useFetchCategorias';
import { IProduto } from '../../../../interfaces/IProduto';

interface CardapioIFoodProps {
    marketplaceUuid: string;
    onImportar?: () => void;
}

// Opção genérica de um seletor de vínculo (produto Space Food ou subelemento de pizza).
interface OpcaoSelecionavel {
    uuid: string;
    descricao: string;
    grupo?: string;
    tipoElemento?: TipoElementoVinculo;
}

const formatarValor = (valor?: number): string => {
    if (valor === undefined || valor === null) return '';
    return valor.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
};

const formatarPreco = (preco?: IPrecoIFood): string => formatarValor(preco?.value);

const ChipStatus = ({ status }: { status?: 'AVAILABLE' | 'UNAVAILABLE' }) => {
    if (status !== 'UNAVAILABLE') return null;
    return <Chip size="small" label="Indisponível" color="default" variant="outlined" />;
};

// Linha genérica: imagem opcional + título/descrição à esquerda, preço/status à direita.
const LinhaItem = ({ nome, descricao, imagem, status, precoTexto, precoChips }: {
    nome: string;
    descricao?: React.ReactNode;
    imagem?: string;
    status?: 'AVAILABLE' | 'UNAVAILABLE';
    precoTexto?: string;
    precoChips?: { label: string; valor: string }[];
}) => (
    <Box sx={{ display: 'flex', gap: 1.5, padding: '0.75rem 0', opacity: status === 'UNAVAILABLE' ? 0.55 : 1 }}>
        {imagem && <Avatar variant="rounded" src={imagem} alt={nome} sx={{ width: 48, height: 48 }} />}
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>{nome}</Typography>
                <ChipStatus status={status} />
            </Box>
            {descricao && (
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.25 }}>
                    {descricao}
                </Typography>
            )}
            {precoChips && precoChips.length > 0 && (
                <Stack direction="row" spacing={0.75} sx={{ mt: 0.75, flexWrap: 'wrap', rowGap: 0.75 }}>
                    {precoChips.map(chip => (
                        <Chip key={chip.label} size="small" variant="outlined"
                            label={<span><b>{chip.label}</b>&nbsp;{chip.valor}</span>} />
                    ))}
                </Stack>
            )}
        </Box>
        {precoTexto && (
            <Typography variant="subtitle2" sx={{ fontWeight: 600, whiteSpace: 'nowrap' }}>
                {precoTexto}
            </Typography>
        )}
    </Box>
);

// Seletor genérico de vínculo: escolhe uma opção (produto ou subelemento) e informa o pai.
const VincularElemento = ({ label, options, valorUuid, onSelecionar, desabilitado }: {
    label: string;
    options: OpcaoSelecionavel[];
    valorUuid?: string;
    onSelecionar: (opcao: OpcaoSelecionavel | null) => void;
    desabilitado?: boolean;
}) => {
    const selecionado = valorUuid ? (options.find(opcao => opcao.uuid === valorUuid) ?? null) : null;

    // Vínculo existe mas o destino não está na lista (ex.: produto/elemento excluído).
    const vinculadoInexistente = !!valorUuid && !selecionado;

    return (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, pb: 1 }}>
            <Autocomplete
                size="small"
                sx={{ flex: 1, maxWidth: 460 }}
                options={options}
                value={selecionado}
                disabled={desabilitado}
                getOptionLabel={(opcao) => opcao.descricao}
                groupBy={(opcao) => opcao.grupo ?? ''}
                isOptionEqualToValue={(opcao, value) => opcao.uuid === value.uuid}
                noOptionsText="Nenhuma opção encontrada"
                onChange={(_event, novoValor) => onSelecionar(novoValor)}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        label={label}
                        placeholder="Selecione"
                        error={vinculadoInexistente}
                        helperText={vinculadoInexistente ? 'Vínculo não encontrado na lista' : undefined}
                    />
                )}
            />
            {selecionado && (
                <Chip size="small" color="success" variant="outlined" label="Vinculado" />
            )}
        </Box>
    );
};

// Subelementos (sabor/borda/massa) de um produto pizza, para o seletor de vínculo.
const elementosDoProduto = (produto?: IProduto): OpcaoSelecionavel[] => {
    if (!produto) return [];
    const lista: OpcaoSelecionavel[] = [];
    (produto.sabores ?? []).forEach(sabor => sabor.uuid && lista.push({ uuid: sabor.uuid, descricao: sabor.descricao, grupo: 'Sabores', tipoElemento: TipoElementoVinculo.sabor }));
    (produto.bordas ?? []).forEach(borda => borda.uuid && lista.push({ uuid: borda.uuid, descricao: borda.descricao, grupo: 'Bordas', tipoElemento: TipoElementoVinculo.borda }));
    (produto.massas ?? []).forEach(massa => massa.uuid && lista.push({ uuid: massa.uuid, descricao: massa.descricao, grupo: 'Massas', tipoElemento: TipoElementoVinculo.massa }));
    return lista;
};

// API de vínculo passada aos componentes filhos.
interface VinculoApi {
    produtos: OpcaoSelecionavel[];
    produtosPorUuid: Map<string, IProduto>;
    produtoVinculoPorCodigo: Map<string, IVinculoMarketplaceProduto>;
    subVinculoPorChave: Map<string, IVinculoMarketplaceProduto>;
    vincularProduto: (codigo: string, nome: string | undefined, produtoUuid: string) => void;
    vincularSub: (tipoElemento: TipoElementoVinculo, codigo: string, nome: string | undefined, tamanhoCodigo: string, produtoUuid: string, elementoUuid: string) => void;
    desvincular: (vinculo?: IVinculoMarketplaceProduto) => void;
    salvando: boolean;
}

// Categoria comum: lista os itens; no modo de vínculo, cada item ganha um seletor de produto.
const ItensDefault = ({ items, api, modoEdicao }: { items: IItemIFood[]; api: VinculoApi; modoEdicao: boolean }) => (
    <Box sx={{ padding: '0 1.25rem' }}>
        {items.map((item, index) => {
            const codigo = item.id ?? item.externalCode;
            const vinculo = codigo ? api.produtoVinculoPorCodigo.get(codigo) : undefined;
            return (
                <Box key={item.id ?? item.externalCode ?? index}>
                    {index > 0 && <Divider />}
                    <LinhaItem
                        nome={item.name ?? item.externalCode ?? 'Item'}
                        descricao={item.description}
                        imagem={item.imagePath}
                        status={item.status}
                        precoTexto={formatarPreco(item.price)}
                    />
                    {modoEdicao && codigo && (
                        <VincularElemento
                            label="Vincular a produto Space Food"
                            options={api.produtos}
                            valorUuid={vinculo?.produtoUuid}
                            desabilitado={api.salvando}
                            onSelecionar={(opcao) => {
                                if (opcao) api.vincularProduto(codigo, item.name, opcao.uuid);
                                else api.desvincular(vinculo);
                            }}
                        />
                    )}
                </Box>
            );
        })}
    </Box>
);

// Exibição read-only de um grupo de opções (fallback quando não identificamos os tamanhos).
const GrupoOpcoesPizza = ({ grupo, ehSabores, ehTamanhos, tamanhosOrdenados, precoPorSabor }: {
    grupo: IGrupoOpcoesIFood;
    ehSabores: boolean;
    ehTamanhos: boolean;
    tamanhosOrdenados: IOpcaoIFood[];
    precoPorSabor: Map<string, Map<string, number>>;
}) => {
    const opcoes = grupo.options ?? [];
    if (opcoes.length === 0) return null;

    if (ehTamanhos) {
        return (
            <Accordion disableGutters defaultExpanded={false} sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMore />}>
                    <Typography variant="subtitle2">{grupo.name ?? 'Tamanho'}</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>({opcoes.length})</Typography>
                </AccordionSummary>
                <AccordionDetails>
                    <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
                        {opcoes.map(o => (
                            <Chip key={o.id} label={o.name} variant="outlined"
                                color={o.status === 'UNAVAILABLE' ? 'default' : 'primary'} />
                        ))}
                    </Stack>
                </AccordionDetails>
            </Accordion>
        );
    }

    return (
        <Accordion disableGutters defaultExpanded={false} sx={{ boxShadow: 'none', '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMore />}>
                <Typography variant="subtitle2">{grupo.name ?? 'Opções'}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>({opcoes.length})</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ pt: 0 }}>
                {opcoes.map((opcao, index) => {
                    if (ehSabores) {
                        const mapa = opcao.id ? precoPorSabor.get(opcao.id) : undefined;
                        const chips = tamanhosOrdenados
                            .map(t => ({ label: t.name ?? 'Tam.', valor: formatarValor(mapa?.get(t.id as string)) }))
                            .filter(c => c.valor);
                        return (
                            <Box key={opcao.id ?? index}>
                                {index > 0 && <Divider />}
                                <LinhaItem nome={opcao.name ?? 'Sabor'} descricao={opcao.description} imagem={opcao.imagePath} status={opcao.status} precoChips={chips} />
                            </Box>
                        );
                    }
                    const valor = opcao.price?.value ?? 0;
                    return (
                        <Box key={opcao.id ?? index}>
                            {index > 0 && <Divider />}
                            <LinhaItem nome={opcao.name ?? 'Opção'} imagem={opcao.imagePath} status={opcao.status} precoTexto={valor > 0 ? `+ ${formatarValor(valor)}` : 'Incluído'} />
                        </Box>
                    );
                })}
            </AccordionDetails>
        </Accordion>
    );
};

// Mapeamento das opções (sabor/borda/massa) de UM tamanho, quando o tamanho já tem produto vinculado.
const SubelementosDoTamanho = ({ tamanhoCodigo, produtoUuid, grupos, api }: {
    tamanhoCodigo: string;
    produtoUuid: string;
    grupos: IGrupoOpcoesIFood[];
    api: VinculoApi;
}) => {
    const produto = api.produtosPorUuid.get(produtoUuid);
    const opcoesElemento = useMemo(() => elementosDoProduto(produto), [produto]);

    if (opcoesElemento.length === 0) {
        return (
            <Alert severity="warning" variant="outlined" sx={{ mt: 1 }}>
                O produto vinculado não possui sabores/bordas/massas para mapear.
            </Alert>
        );
    }

    return (
        <Box sx={{ mt: 1 }}>
            {grupos.map(grupo => {
                const opcoes = grupo.options ?? [];
                if (opcoes.length === 0) return null;
                return (
                    <Box key={grupo.id ?? grupo.name} sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                            {grupo.name ?? 'Opções'}
                        </Typography>
                        {opcoes.map(opcao => {
                            const codigoOpcao = opcao.id ?? opcao.externalCode;
                            if (!codigoOpcao) return null;
                            const vinculo = api.subVinculoPorChave.get(`${tamanhoCodigo}::${codigoOpcao}`);
                            return (
                                <VincularElemento
                                    key={codigoOpcao}
                                    label={opcao.name ?? 'Opção'}
                                    options={opcoesElemento}
                                    valorUuid={vinculo?.elementoUuid}
                                    desabilitado={api.salvando}
                                    onSelecionar={(escolhido) => {
                                        if (escolhido && escolhido.tipoElemento) {
                                            api.vincularSub(escolhido.tipoElemento, codigoOpcao, opcao.name, tamanhoCodigo, produtoUuid, escolhido.uuid);
                                        } else {
                                            api.desvincular(vinculo);
                                        }
                                    }}
                                />
                            );
                        })}
                    </Box>
                );
            })}
        </Box>
    );
};

const CategoriaPizza = ({ item, api, modoEdicao }: { item: IItemIFood; api: VinculoApi; modoEdicao: boolean }) => {
    const grupos: IGrupoOpcoesIFood[] = item.optionGroups ?? [];
    const modifiers = item.customizationModifiers ?? [];

    if (grupos.length === 0) {
        return <Typography variant="body2" color="text.secondary" sx={{ px: '1.25rem' }}>Nenhuma configuração encontrada.</Typography>;
    }

    const idsSabores = new Set(modifiers.map(m => m.customizationOptionId).filter(Boolean));
    const idsTamanhos = new Set(modifiers.map(m => m.parentCustomizationOptionId).filter(Boolean));

    const grupoTamanhos = grupos.find(g => (g.options ?? []).some(o => o.id && idsTamanhos.has(o.id)));
    const tamanhosOrdenados = (grupoTamanhos?.options ?? []).filter(o => o.id && idsTamanhos.has(o.id));

    const precoPorSabor = new Map<string, Map<string, number>>();
    modifiers.forEach(m => {
        if (!m.customizationOptionId || !m.parentCustomizationOptionId || m.price === undefined) return;
        if (!precoPorSabor.has(m.customizationOptionId)) precoPorSabor.set(m.customizationOptionId, new Map());
        precoPorSabor.get(m.customizationOptionId)!.set(m.parentCustomizationOptionId, m.price);
    });

    // Visualização read-only (padrão) — mostra tudo: tamanhos, sabores (com preços) e borda/massa.
    const visualizacao = (
        <Box>
            {grupos.map(grupo => (
                <GrupoOpcoesPizza
                    key={grupo.id ?? grupo.name}
                    grupo={grupo}
                    ehSabores={(grupo.options ?? []).some(o => o.id && idsSabores.has(o.id))}
                    ehTamanhos={grupo === grupoTamanhos}
                    tamanhosOrdenados={tamanhosOrdenados}
                    precoPorSabor={precoPorSabor}
                />
            ))}
        </Box>
    );

    if (!modoEdicao) {
        return visualizacao;
    }

    // Sem tamanhos identificáveis: cai no modo read-only (não quebra a visualização).
    if (!grupoTamanhos || tamanhosOrdenados.length === 0) {
        return (
            <Box>
                <Alert severity="warning" variant="outlined" sx={{ mb: 1, mx: '1.25rem' }}>
                    Não foi possível identificar os tamanhos desta pizza para o vínculo. Exibindo apenas visualização.
                </Alert>
                {visualizacao}
            </Box>
        );
    }

    // Grupos de sabor/borda/massa (tudo que não é o grupo de tamanhos).
    const gruposSubelementos = grupos.filter(grupo => grupo !== grupoTamanhos);

    return (
        <Box sx={{ px: '1.25rem' }}>
            <Alert severity="info" variant="outlined" sx={{ mb: 1 }}>
                Vincule cada <b>tamanho</b> a um produto Space Food. Depois, mapeie os sabores/bordas/massas
                daquele tamanho aos do produto.
            </Alert>
            {tamanhosOrdenados.map(tamanho => {
                const codigoTamanho = tamanho.id ?? tamanho.externalCode;
                if (!codigoTamanho) return null;
                const vinculoTamanho = api.produtoVinculoPorCodigo.get(codigoTamanho);
                const produtoUuid = vinculoTamanho?.produtoUuid;

                return (
                    <Accordion
                        key={codigoTamanho}
                        disableGutters
                        defaultExpanded={false}
                        slotProps={{ transition: { unmountOnExit: true } }}
                        sx={{ mb: 1, border: 1, borderColor: 'divider', borderRadius: '8px', boxShadow: 'none', '&:before': { display: 'none' } }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, pr: 1 }}>
                                <Typography variant="subtitle2">{tamanho.name ?? 'Tamanho'}</Typography>
                                <Box sx={{ flex: 1 }} />
                                {produtoUuid && <Chip size="small" color="success" variant="outlined" label="Vinculado" />}
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            <VincularElemento
                                label="Vincular tamanho a produto Space Food"
                                options={api.produtos}
                                valorUuid={produtoUuid}
                                desabilitado={api.salvando}
                                onSelecionar={(opcao) => {
                                    if (opcao) api.vincularProduto(codigoTamanho, tamanho.name, opcao.uuid);
                                    else api.desvincular(vinculoTamanho);
                                }}
                            />
                            {produtoUuid ? (
                                <SubelementosDoTamanho
                                    tamanhoCodigo={codigoTamanho}
                                    produtoUuid={produtoUuid}
                                    grupos={gruposSubelementos}
                                    api={api}
                                />
                            ) : (
                                <Typography variant="body2" color="text.secondary">
                                    Vincule um produto ao tamanho para mapear os sabores.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};

export const CardapioIFood = ({ marketplaceUuid, onImportar }: CardapioIFoodProps) => {
    const { buscarCardapio } = useIFood();
    const { listarVinculos, salvarVinculo, excluirVinculo, loading: salvandoVinculo } = useVinculosMarketplace();
    const { categorias: categoriasSpaceFood } = useFetchCategorias();
    const [carregando, setCarregando] = useState(true);
    const [categorias, setCategorias] = useState<ICategoriaIFood[] | null>(null);
    const [vinculos, setVinculos] = useState<IVinculoMarketplaceProduto[]>([]);
    const [modoEdicao, setModoEdicao] = useState(false);

    useEffect(() => {
        let ativo = true;
        (async () => {
            setCarregando(true);
            const resposta = await buscarCardapio(marketplaceUuid);
            if (ativo) {
                setCategorias(resposta);
                setCarregando(false);
            }
        })();
        return () => { ativo = false; };
    }, [marketplaceUuid]);

    useEffect(() => {
        let ativo = true;
        (async () => {
            const lista = await listarVinculos('IFOOD', marketplaceUuid);
            if (ativo) {
                setVinculos(lista);
            }
        })();
        return () => { ativo = false; };
    }, [marketplaceUuid]);

    // Todos os produtos disponíveis (qualquer tipo, incluindo pizza) e um índice por uuid.
    const produtos = useMemo<OpcaoSelecionavel[]>(() => {
        if (!categoriasSpaceFood) return [];
        const lista: OpcaoSelecionavel[] = [];
        categoriasSpaceFood.forEach(categoria => {
            (categoria.produtos ?? []).forEach(produto => {
                if (!produto.uuid) return;
                lista.push({ uuid: produto.uuid, descricao: produto.descricao, grupo: categoria.descricao });
            });
        });
        return lista;
    }, [categoriasSpaceFood]);

    const produtosPorUuid = useMemo(() => {
        const mapa = new Map<string, IProduto>();
        categoriasSpaceFood?.forEach(categoria => {
            (categoria.produtos ?? []).forEach(produto => {
                if (produto.uuid) mapa.set(produto.uuid, produto);
            });
        });
        return mapa;
    }, [categoriasSpaceFood]);

    const produtoVinculoPorCodigo = useMemo(() => {
        const mapa = new Map<string, IVinculoMarketplaceProduto>();
        vinculos.forEach(vinculo => {
            if ((vinculo.tipoElemento ?? TipoElementoVinculo.produto) === TipoElementoVinculo.produto) {
                mapa.set(vinculo.codigoItemExterno, vinculo);
            }
        });
        return mapa;
    }, [vinculos]);

    const subVinculoPorChave = useMemo(() => {
        const mapa = new Map<string, IVinculoMarketplaceProduto>();
        vinculos.forEach(vinculo => {
            if ((vinculo.tipoElemento ?? TipoElementoVinculo.produto) !== TipoElementoVinculo.produto && vinculo.codigoTamanhoExterno) {
                mapa.set(`${vinculo.codigoTamanhoExterno}::${vinculo.codigoItemExterno}`, vinculo);
            }
        });
        return mapa;
    }, [vinculos]);

    const chaveVinculo = (vinculo: IVinculoMarketplaceProduto) =>
        `${vinculo.tipoElemento ?? TipoElementoVinculo.produto}::${vinculo.codigoTamanhoExterno ?? ''}::${vinculo.codigoItemExterno}`;

    const substituirVinculo = (salvo: IVinculoMarketplaceProduto) => {
        setVinculos(anteriores => [...anteriores.filter(vinculo => chaveVinculo(vinculo) !== chaveVinculo(salvo)), salvo]);
    };

    const vincularProduto = async (codigo: string, nome: string | undefined, produtoUuid: string) => {
        const salvo = await salvarVinculo({
            tipoMarketplace: TipoMarketplace.IFOOD,
            marketplaceUuid,
            tipoElemento: TipoElementoVinculo.produto,
            codigoItemExterno: codigo,
            nomeItemExterno: nome,
            produtoUuid
        });
        if (salvo) substituirVinculo(salvo);
    };

    const vincularSub = async (
        tipoElemento: TipoElementoVinculo,
        codigo: string,
        nome: string | undefined,
        tamanhoCodigo: string,
        produtoUuid: string,
        elementoUuid: string
    ) => {
        const salvo = await salvarVinculo({
            tipoMarketplace: TipoMarketplace.IFOOD,
            marketplaceUuid,
            tipoElemento,
            codigoItemExterno: codigo,
            codigoTamanhoExterno: tamanhoCodigo,
            nomeItemExterno: nome,
            produtoUuid,
            elementoUuid
        });
        if (salvo) substituirVinculo(salvo);
    };

    const desvincular = async (vinculo?: IVinculoMarketplaceProduto) => {
        if (!vinculo?.uuid) return;
        const removido = await excluirVinculo(vinculo.uuid);
        if (removido) {
            setVinculos(anteriores => anteriores.filter(atual => atual.uuid !== vinculo.uuid));
        }
    };

    const api: VinculoApi = {
        produtos,
        produtosPorUuid,
        produtoVinculoPorCodigo,
        subVinculoPorChave,
        vincularProduto,
        vincularSub,
        desvincular,
        salvando: salvandoVinculo
    };

    if (carregando) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!categorias || categorias.length === 0) {
        return (
            <Alert severity="info" sx={{ marginTop: '1rem' }}>
                Nenhuma categoria encontrada no cardápio do iFood.
            </Alert>
        );
    }

    return (
        <Box sx={{ marginTop: '1rem', width: '100%' }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, mb: 1, flexWrap: 'wrap' }}>
                <Typography variant="body2" color="text.secondary">
                    Cardápio do iFood — editado no portal do iFood; a Space Food não o sincroniza.
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                    {onImportar && (
                        <Button variant="outlined" size="small" startIcon={<CloudDownload />} onClick={onImportar}>
                            Importar para a Space Food
                        </Button>
                    )}
                    <FormControlLabel
                        control={<Switch checked={modoEdicao} onChange={(_event, marcado) => setModoEdicao(marcado)} />}
                        label="Modo de vínculo"
                    />
                </Box>
            </Box>
            {modoEdicao && (
                <Alert severity="info" sx={{ marginBottom: '1rem' }}>
                    <b>Modo de vínculo ativo.</b> Relacione cada item (e cada tamanho/sabor de pizza) do iFood a um
                    produto da Space Food. Quando um pedido chegar, o item vinculado passa a funcionar em KDS, estoque,
                    comanda e financeiro como um produto nativo. Itens sem vínculo continuam funcionando como hoje.
                </Alert>
            )}
            {categorias.map(categoria => {
                const ehPizza = categoria.template === 'PIZZA';
                const itemPizza = ehPizza ? categoria.items?.[0] : undefined;
                const qtdItens = ehPizza
                    ? (itemPizza?.optionGroups?.reduce((total, g) => total + (g.options?.length ?? 0), 0) ?? 0)
                    : (categoria.items?.length ?? 0);
                const legenda = ehPizza ? 'Pizza' : `${qtdItens} ${qtdItens === 1 ? 'item' : 'itens'}`;

                return (
                    <Accordion
                        key={categoria.id}
                        defaultExpanded={false}
                        disableGutters
                        slotProps={{ transition: { unmountOnExit: true } }}
                        sx={{ mb: 1.5, borderRadius: '12px', border: 1, borderColor: 'divider', boxShadow: 'none', '&:before': { display: 'none' } }}
                    >
                        <AccordionSummary expandIcon={<ExpandMore />}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1, pr: 1 }}>
                                <Typography variant="h6" sx={{ fontSize: '1.05rem' }}>{categoria.name}</Typography>
                                {ehPizza && <Chip size="small" label="Pizza" variant="outlined" />}
                                <Box sx={{ flex: 1 }} />
                                <Typography variant="body2" color="text.secondary">{legenda}</Typography>
                                <ChipStatus status={categoria.status} />
                            </Box>
                        </AccordionSummary>
                        <AccordionDetails sx={{ pt: 0 }}>
                            {ehPizza && itemPizza ? (
                                <CategoriaPizza item={itemPizza} api={api} modoEdicao={modoEdicao} />
                            ) : categoria.items && categoria.items.length > 0 ? (
                                <ItensDefault items={categoria.items} api={api} modoEdicao={modoEdicao} />
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ px: '1.25rem' }}>
                                    Nenhum item nesta categoria.
                                </Typography>
                            )}
                        </AccordionDetails>
                    </Accordion>
                );
            })}
        </Box>
    );
};
